/**
 * Where a price in a quote came from.
 *
 * It travels with the line all the way into the saved revision on purpose:
 * "we guessed this one" is exactly the sort of thing that matters most six
 * weeks later, when somebody asks why the invoice does not match the quote
 * and the screen that first said so is long gone.
 */
export type QuotePriceSource = 'ProviderRate' | 'Estimated' | 'Manual';

export const QUOTE_PRICE_SOURCE: Record<'PROVIDER_RATE' | 'ESTIMATED' | 'MANUAL', QuotePriceSource> = {
  /** Priced from the provider's own uploaded rate card. */
  PROVIDER_RATE: 'ProviderRate',
  /** Guessed by the model, because the rate card had nothing for it. */
  ESTIMATED: 'Estimated',
  /** Typed by the provider on this quote. */
  MANUAL: 'Manual',
};

/** One line the assistant drafted -- already priced off the caller's rate card server-side. */
export interface IQuoteDraftLine {
  title: string;
  description?: string;
  /** "ud", "h", "m2"... whatever the provider's own rate card uses. */
  unit: string;
  quantity: number;
  unitPrice: number;
  /** quantity x unitPrice, rounded once by the server. */
  amount: number;
  priceSource: QuotePriceSource;
  /** The rate-card entry this was priced from, when it was priced from one. */
  catalogKey?: string;
}

/**
 * What POST provider/quote-drafts hands back. It writes nothing: no revision,
 * no booking, nothing anybody can be charged for -- see the backend's
 * QuoteDraftsController. Turning this into a real quote is still a deliberate
 * act by the provider in the editor.
 */
export interface IQuoteDraftResponse {
  lines: IQuoteDraftLine[];
  /** What the model assumed to get there. Shown, because a wrong assumption stated out loud is the cheapest possible catch. */
  notes?: string;
  /** How many lines had to be guessed at. The screen leads with this. */
  estimatedLineCount: number;
  total: number;
}

/**
 * One line of the quote as it stands, sent along so a spoken change can be
 * applied to it.
 *
 * What the screen is holding is what travels: a quote being edited has not
 * been saved anywhere yet, so there is nothing on the server to read. The
 * prices here are only context -- the real ones are resolved again from the
 * provider's own rate card server-side.
 */
export interface IQuoteDraftContextLine {
  title: string;
  description?: string;
  unit: string;
  quantity: number;
  unitPrice: number;
  priceSource?: QuotePriceSource;
  catalogKey?: string;
}

export interface ICreateQuoteDraftRequest {
  /** The job in the provider's own words. May be empty when `instruction` carries the ask. */
  description: string;
  /** "fontanería", "electricidad"... a hint that narrows assumptions, never a price. */
  trade?: string;
  /**
   * A change to make to the lines below, in the provider's own words: "quita
   * el transporte", "sube la hora a cincuenta". Sent with `currentLines`, and
   * then the description is not needed -- the instruction is the ask.
   */
  instruction?: string;
  /** The quote as it stands, when `instruction` is set. */
  currentLines?: IQuoteDraftContextLine[];
}
